import type { OpenAIRealtimeVoice } from "@/types/user"
import type { VoiceAudioProfile } from "@/types/voice-chat"

import { BaseApi } from "@/lib/base-api"

export interface OpenAISetupVoice {
  voice: OpenAIRealtimeVoice
  name: string
  preview_available: boolean
  preview_speed: number
}

export type VoiceInterviewOptionsResponse = {
  provider: "openai"
  flow: "setup"
  default_voice: OpenAIRealtimeVoice
  voices: OpenAISetupVoice[]
  audio_profile: VoiceAudioProfile
}

export interface SpeakVoiceRequest {
  message_id: number
}

export interface VoicePreviewResponse {
  audio_base64: string
  content_type: string
}

export const AUDIO_GUIDE_SCRIPTS = ["overview", "history"] as const

export type VoiceGuideScript = (typeof AUDIO_GUIDE_SCRIPTS)[number]
export const AUDIO_GUIDE_TONES = [
  "friendly",
  "professional",
  "casual",
  "enthusiastic",
  "calm",
] as const
export type VoiceGuideTone = (typeof AUDIO_GUIDE_TONES)[number]

export interface AudioGuideResponse {
  voice: string
  transcript: string
  audio_base64: string
  content_type: string
}

export interface CreateAudioGuideRequest {
  entity_ref: string
  script: VoiceGuideScript
  tone?: VoiceGuideTone
}

export type VoiceInterviewFlow = "setup"

export const AUDIO_GUIDE_SCRIPT_LABELS: Record<VoiceGuideScript, string> = {
  overview: "Overview",
  history: "History",
}

export const DEFAULT_VOICE_SPEED = 1

export function normalizedVoiceSpeed(speed?: number | null): number {
  return typeof speed === "number" && Number.isFinite(speed)
    ? Math.max(0.7, Math.min(1.2, speed))
    : DEFAULT_VOICE_SPEED
}

export class VoiceApi extends BaseApi {
  async createAudioGuide(
    request: CreateAudioGuideRequest
  ): Promise<AudioGuideResponse> {
    return await this.post<AudioGuideResponse, CreateAudioGuideRequest>(
      "/api/voice/audio_guides",
      request
    )
  }

  async speak(messageId: number): Promise<VoicePreviewResponse> {
    return await this.post<VoicePreviewResponse, SpeakVoiceRequest>(
      "/api/voice/speak",
      {
        message_id: messageId,
      }
    )
  }
}
